import { API_BASE_URL } from "../config";

const GENERIC_ERROR_MESSAGES = new Set([
  "Failed to fetch",
  "NetworkError when attempting to fetch resource.",
  "Internal Server Error"
]);

export interface ApiError extends Error {
  details?: Record<string, unknown>;
  statusCode?: number;
}

export async function raiseApiError(response: Response, fallbackMessage: string): Promise<never> {
  let message = fallbackMessage;
  let details: Record<string, unknown> | undefined;

  const contentType = response.headers.get("content-type") ?? "";

  try {
    if (contentType.includes("application/json")) {
      const data = (await response.json()) as Record<string, unknown> | null;
      if (data && typeof data === "object") {
        details = data;
        const maybeMessage = data.message;
        if (typeof maybeMessage === "string" && maybeMessage.trim().length > 0) {
          message = maybeMessage.trim();
        }
      }
    } else {
      const text = await response.text();
      if (typeof text === "string" && text.trim().length > 0) {
        message = text.trim();
      }
    }
  } catch {
    // ignore parse errors and fall back to default message
  }

  const error: ApiError = new Error(message);
  error.statusCode = response.status;
  if (details) {
    error.details = details;
  }

  throw error;
}

export function resolveErrorMessage(error: unknown): string | null {
  if (!error) {
    return null;
  }

  if (typeof error === "string") {
    const trimmed = error.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (error instanceof Error) {
    const trimmed = error.message?.trim();
    if (!trimmed || GENERIC_ERROR_MESSAGES.has(trimmed)) {
      return null;
    }
    return trimmed;
  }

  if (typeof error === "object") {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string") {
      const trimmed = message.trim();
      if (trimmed.length > 0 && !GENERIC_ERROR_MESSAGES.has(trimmed)) {
        return trimmed;
      }
    }

    const details = (error as { details?: unknown }).details;
    if (details && typeof details === "object" && "message" in details) {
      const detailedMessage = (details as { message?: unknown }).message;
      if (typeof detailedMessage === "string") {
        const trimmed = detailedMessage.trim();
        if (trimmed.length > 0 && !GENERIC_ERROR_MESSAGES.has(trimmed)) {
          return trimmed;
        }
      }
    }
  }

  return null;
}

/**
 * Build API URL with proper handling of empty base URL (for Azure SWA)
 * 
 * @param path - API path (e.g., "/api/users")
 * @param params - Optional query parameters
 * @returns Complete URL string
 */
export function buildApiUrl(path: string, params?: Record<string, string | number | boolean>): string {
  let url: string;

  if (API_BASE_URL) {
    // With base URL: use URL constructor
    const urlObj = new URL(path, API_BASE_URL);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        urlObj.searchParams.set(key, String(value));
      });
    }
    url = urlObj.toString();
  } else {
    // Without base URL (Azure SWA): build relative URL manually
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        searchParams.set(key, String(value));
      });
      const queryString = searchParams.toString();
      url = queryString ? `${path}?${queryString}` : path;
    } else {
      url = path;
    }
  }

  return url;
}
